import { type ViewContext, preparesForNmt } from '@shkola/api';
import {
  Bell,
  BookHeart,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  FileSignature,
  FlaskConical,
  Gem,
  Globe,
  GraduationCap,
  HeartHandshake,
  House,
  Images,
  LayoutGrid,
  ListChecks,
  type LucideIcon,
  Megaphone,
  MessagesSquare,
  NotebookPen,
  Paperclip,
  PartyPopper,
  PenLine,
  Puzzle,
  Rocket,
  Settings,
  Settings2,
  ShieldAlert,
  Sprout,
  Star,
  Telescope,
  Trophy,
  Truck,
  Utensils,
  Volleyball,
  Vote,
} from 'lucide-react';

export type NavKey =
  | 'home'
  | 'schedule'
  | 'journal'
  | 'grades'
  | 'homework'
  | 'announcements'
  | 'notifications'
  | 'alarm'
  | 'messages'
  | 'requests'
  | 'meetings'
  | 'events'
  | 'canteen'
  | 'polls'
  | 'achievements'
  | 'materials'
  | 'album'
  | 'quizzes'
  | 'care'
  | 'lab'
  | 'clubs'
  | 'tree'
  | 'nmt'
  | 'planning'
  | 'season'
  | 'yearbook'
  | 'future'
  | 'abroad'
  | 'abroadSchool'
  | 'forms'
  | 'rewards'
  | 'moving'
  | 'letters'
  | 'life'
  | 'settings'
  | 'admin';

export const NAV: Record<NavKey, { to: string; icon: LucideIcon }> = {
  home: { to: '/', icon: House },
  schedule: { to: '/schedule', icon: CalendarDays },
  journal: { to: '/journal', icon: NotebookPen },
  grades: { to: '/grades', icon: Star },
  homework: { to: '/homework', icon: ListChecks },
  announcements: { to: '/announcements', icon: Megaphone },
  notifications: { to: '/notifications', icon: Bell },
  alarm: { to: '/alarm', icon: ShieldAlert },
  messages: { to: '/messages', icon: MessagesSquare },
  requests: { to: '/requests', icon: ClipboardList },
  meetings: { to: '/meetings', icon: CalendarClock },
  events: { to: '/events', icon: PartyPopper },
  canteen: { to: '/canteen', icon: Utensils },
  polls: { to: '/polls', icon: Vote },
  achievements: { to: '/achievements', icon: Trophy },
  materials: { to: '/materials', icon: Paperclip },
  album: { to: '/album', icon: Images },
  quizzes: { to: '/quizzes', icon: Puzzle },
  care: { to: '/care', icon: HeartHandshake },
  lab: { to: '/lab', icon: FlaskConical },
  clubs: { to: '/clubs', icon: Volleyball },
  tree: { to: '/tree', icon: Sprout },
  nmt: { to: '/nmt', icon: GraduationCap },
  planning: { to: '/planning', icon: CalendarRange },
  season: { to: '/season', icon: Rocket },
  yearbook: { to: '/yearbook', icon: BookHeart },
  future: { to: '/future', icon: Telescope },
  abroad: { to: '/abroad', icon: Globe },
  abroadSchool: { to: '/abroad', icon: Globe },
  forms: { to: '/forms', icon: FileSignature },
  rewards: { to: '/rewards', icon: Gem },
  moving: { to: '/moving', icon: Truck },
  letters: { to: '/letters', icon: PenLine },
  life: { to: '/life', icon: LayoutGrid },
  settings: { to: '/settings', icon: Settings },
  admin: { to: '/admin', icon: Settings2 },
};

export type NavGroup = 'study' | 'lessons' | 'school' | 'class';

/**
 * The menu of a role (design v4): the sections everyone opens every day (`pinned`) and the two groups,
 * all open, always in sight; rarer sections are the folders of «Шкільне життя» (`hub`); notifications
 * are the bell on top, every section is in the search.
 */
export interface NavLayout {
  pinned: NavKey[];
  groups: ReadonlyArray<{ label: NavGroup; items: NavKey[] }>;
  footer: NavKey[];
  hub: NavKey[];
}

const only = <T>(on: boolean | undefined, ...items: T[]): T[] => (on ? items : []);

/** `abroad`: the family lives abroad, or the school has such students to show. */
export function navLayout(ctx: ViewContext | undefined, abroad: boolean): NavLayout {
  switch (ctx?.kind) {
    case 'student':
      return {
        pinned: [
          'home',
          ...only(abroad, 'abroad' as const),
          'schedule',
          'homework',
          'grades',
          'messages',
          'announcements',
        ],
        groups: [
          {
            label: 'study',
            items: [
              'quizzes',
              'materials',
              'lab',
              'tree',
              ...only(preparesForNmt(ctx.child.class?.grade), 'nmt' as const),
              'future',
            ],
          },
          { label: 'school', items: ['events', 'clubs', 'life'] },
        ],
        footer: ['care', 'alarm'],
        hub: [
          'season',
          'rewards',
          'achievements',
          'album',
          'yearbook',
          'forms',
          'letters',
          'clubs',
          'events',
          'canteen',
        ],
      };
    case 'guardian':
      return {
        pinned: [
          'home',
          ...only(abroad, 'abroad' as const),
          'schedule',
          'grades',
          'homework',
          'messages',
          'announcements',
        ],
        groups: [
          {
            label: 'study',
            items: [
              'quizzes',
              'materials',
              'lab',
              'tree',
              ...only(preparesForNmt(ctx.child.class?.grade), 'nmt' as const),
              'future',
            ],
          },
          { label: 'school', items: ['meetings', 'requests', 'forms', 'life'] },
        ],
        footer: ['care', 'alarm'],
        hub: [
          'events',
          'clubs',
          'canteen',
          'polls',
          'season',
          'rewards',
          'achievements',
          'album',
          'yearbook',
          'moving',
        ],
      };
    case 'teacher':
      return {
        pinned: ['home', 'schedule', 'journal', 'homework', 'messages', 'announcements'],
        groups: [
          { label: 'lessons', items: ['planning', 'quizzes', 'materials', 'lab'] },
          {
            label: 'class',
            items: [
              'meetings',
              // statements come to the homeroom teacher of the class
              ...only(Boolean(ctx.homeroomClass), 'requests' as const),
              'forms',
              ...only(abroad, 'abroadSchool' as const),
              'life',
            ],
          },
        ],
        footer: ['alarm'],
        // the homeroom teacher leads the class's season
        hub: [
          ...only(Boolean(ctx.homeroomClass), 'season' as const),
          'events',
          'clubs',
          'achievements',
          'album',
          'canteen',
          'polls',
        ],
      };
    case 'admin':
      return {
        pinned: ['home', 'schedule', 'journal', 'messages', 'announcements', 'admin'],
        groups: [
          { label: 'study', items: ['homework', 'quizzes', 'materials', 'lab', 'future'] },
          {
            label: 'school',
            items: [
              'meetings',
              'requests',
              'forms',
              'abroadSchool',
              'moving',
              // bullying reports come to the director
              ...only(ctx.roles.includes('director'), 'care' as const),
              'life',
            ],
          },
        ],
        footer: ['alarm'],
        hub: ['events', 'clubs', 'achievements', 'album', 'canteen', 'polls'],
      };
    case 'psychologist':
      return {
        pinned: ['home', 'care', 'meetings', 'messages', 'announcements'],
        groups: [{ label: 'school', items: ['events', 'clubs', 'canteen'] }],
        footer: ['alarm'],
        hub: [],
      };
    default:
      return { pinned: ['home'], groups: [], footer: [], hub: [] };
  }
}

/** Every section of the role once, in the menu's order: for the search. */
export function allSections(layout: NavLayout): NavKey[] {
  const keys = [
    ...layout.pinned,
    ...layout.groups.flatMap((g) => g.items),
    ...layout.hub,
    'notifications' as const,
    ...layout.footer,
  ];
  return [...new Set(keys)].filter((k) => k !== 'life' || layout.hub.length > 0);
}

/** The section a path belongs to (for the title at the top). */
export function sectionOf(path: string, layout: NavLayout): NavKey | undefined {
  if (path === '/') return 'home';
  if (path === '/settings') return 'settings';
  const keys = allSections(layout);
  return keys.find((k) => k !== 'home' && (path === NAV[k].to || path.startsWith(`${NAV[k].to}/`)));
}
